// src/pages/PatientAppointmentsPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  Avatar,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CalendarMonth as CalendarIcon,
  AccessTime as TimeIcon,
  Event as EventIcon,
  Replay as RescheduleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import axiosInstance from '../axiosInstance';
import { toast } from 'react-toastify';
import { useNavigate, Link as RouterLink } from 'react-router-dom';


const PatientAppointmentsPage = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [tab, setTab] = useState(0); // 0: Próximas, 1: Pasadas, 2: Canceladas
  const navigate = useNavigate();

  const fetchAppointments = async (signal) => {
    try {
      setLoading(true);
      setError('');
      // axiosInstance.baseURL = `${ORIGIN}/api`, por eso omitimos /api aquí
      const { data } = await axiosInstance.get('/patients/appointments/', { signal });

      // Soporta respuesta paginada (DRF) y lista simple
      const list = Array.isArray(data) ? data : (data?.results ?? []);
      setAppointments(list);
    } catch (e) {
      // Cancelación: cubrir Axios y AbortController
      if (e?.name === 'CanceledError' || e?.name === 'AbortError' || e?.code === 'ERR_CANCELED') return;
      console.error(e);
      const msg = e?.response?.data?.detail || 'No se pudieron cargar tus citas. Intenta nuevamente.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const reload = async () => {
    try {
      setReloading(true);
      await fetchAppointments(); // sin signal (llamada manual)
      toast.success('Citas actualizadas');
    } catch {
      // ya manejado en fetchAppointments
    } finally {
      setReloading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchAppointments(controller.signal);
    return () => controller.abort();
  }, []);

  const now = new Date();

  const { upcoming, past, cancelled } = useMemo(() => {
    const upcomingArr = [];
    const pastArr = [];
    const cancelledArr = [];

    appointments.forEach((a) => {
      const start = new Date(a.fecha_hora_cita);
      if (a.estado_cita === 'Cancelada') {
        cancelledArr.push(a);
      } else if (start >= now) {
        upcomingArr.push(a);
      } else {
        pastArr.push(a);
      }
    });

    // Orden: próximas por fecha asc, pasadas por fecha desc, canceladas por fecha desc
    upcomingArr.sort((a, b) => new Date(a.fecha_hora_cita) - new Date(b.fecha_hora_cita));
    pastArr.sort((a, b) => new Date(b.fecha_hora_cita) - new Date(a.fecha_hora_cita));
    cancelledArr.sort((a, b) => new Date(b.fecha_hora_cita) - new Date(a.fecha_hora_cita));

    return { upcoming: upcomingArr, past: pastArr, cancelled: cancelledArr };
  }, [appointments, now]);

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'PPP', { locale: es });
    } catch {
      return 'Fecha no disponible';
    }
  };

  const formatTime = (dateStr) => {
    try {
      return format(new Date(dateStr), 'HH:mm', { locale: es });
    } catch {
      return '--:--';
    }
  };

  const handleCancel = async (appt) => {
    const confirmed = window.confirm(
      `¿Deseas cancelar tu cita con ${appt.medico_nombre} el ${formatDate(appt.fecha_hora_cita)} a las ${formatTime(appt.fecha_hora_cita)}?`
    );
    if (!confirmed) return;

    try {
      // Endpoint de acción personalizada cancelar en el ViewSet:
      // POST /patients/appointments/:id/cancelar/
      await axiosInstance.post(`/patients/appointments/${appt.id_cita}/cancelar/`);
      toast.success('Cita cancelada correctamente');
      await reload();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || e?.response?.data?.error || 'No se pudo cancelar la cita';
      toast.error(msg);
    }
  };

  const handleReschedule = (appointment) => {
    // Redirige al buscador de médicos dentro del portal del paciente
    navigate(`/patient/appointments/${appointment.id_cita}/reschedule`);
  };

  const EmptyState = ({ title, subtitle, action }) => (
    <Box sx={{ py: 6, textAlign: 'center' }}>
      <Typography variant="h6" color="text.primary" gutterBottom>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
      {action && <Box sx={{ mt: 2 }}>{action}</Box>}
    </Box>
  );

  const AppointmentCard = ({ appt, showActions = true }) => (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 3,
        border: 'none',
        boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`,
        '&:hover': {
          boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
        },
        transition: 'box-shadow 0.3s ease',
      }}
    >
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm="auto">
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 56,
                height: 56,
              }}
            >
              {appt.medico_nombre?.charAt(0) || 'D'}
            </Avatar>
          </Grid>

          <Grid item xs>
            <Typography variant="subtitle1" fontWeight="medium">
              {appt.medico_nombre}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {appt.medico_especialidad}
            </Typography>

            {appt.motivo_cita && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {appt.motivo_cita}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} sm="auto" sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
              <EventIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
              <Typography variant="body2" fontWeight="medium">
                {formatDate(appt.fecha_hora_cita)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                mt: 0.5,
              }}
            >
              <TimeIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
              <Typography variant="body2" fontWeight="medium">
                {formatTime(appt.fecha_hora_cita)}
              </Typography>
            </Box>

            <Box sx={{ mt: 1, display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
              <Chip
                label={appt.estado_cita}
                color={
                  appt.estado_cita === 'Confirmada'
                    ? 'success'
                    : appt.estado_cita === 'Cancelada'
                    ? 'default'
                    : 'primary'
                }
                size="small"
                sx={{ borderRadius: 1 }}
              />
            </Box>
          </Grid>
        </Grid>

        {showActions && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<RescheduleIcon />}
                onClick={() => handleReschedule(appt)} 
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Reprogramar
              </Button>
              {appt.estado_cita !== 'Cancelada' && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => handleCancel(appt)}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Cancelar
                </Button>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    }

    if (tab === 0) {
      // Próximas
      if (upcoming.length === 0) {
        return (
          <EmptyState
            title="No tienes citas próximas"
            subtitle="Cuando agendes una cita, aparecerá aquí."
            action={
              <Button variant="contained" sx={{ borderRadius: 3 }} component={RouterLink} to="/patient/doctors">
                Agendar nueva cita
              </Button>
            }
          />
        );
      }
      return (
        <Box>
          {upcoming.map((a) => (
            <AppointmentCard key={a.id_cita} appt={a} />
          ))}
        </Box>
      );
    }

    if (tab === 1) {
      // Pasadas
      if (past.length === 0) {
        return <EmptyState title="No hay citas pasadas" />;
      }
      return (
        <Box>
          {past.map((a) => (
            <AppointmentCard key={a.id_cita} appt={a} showActions={false} />
          ))}
        </Box>
      );
    }

    // Canceladas
    if (cancelled.length === 0) {
      return <EmptyState title="No hay citas canceladas" />;
    }
    return (
      <Box>
        {cancelled.map((a) => (
          <AppointmentCard key={a.id_cita} appt={a} showActions={false} />
        ))}
      </Box>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 4,
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main }}>
              <CalendarIcon />
            </Avatar>
            <Typography variant="h5" fontWeight="bold">
              Mis Citas
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={reload}
              disabled={reloading}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              {reloading ? 'Actualizando...' : 'Actualizar'}
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/patient/doctors')}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Agendar nueva
            </Button>
          </Box>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          aria-label="tabs-citas"
          sx={{
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            mb: 2,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
          }}
        >
          <Tab label={`Próximas (${upcoming.length})`} />
          <Tab label={`Pasadas (${past.length})`} />
          <Tab label={`Canceladas (${cancelled.length})`} />
        </Tabs>

        {renderContent()}
      </Paper>
    </Container>
  );
};

export default PatientAppointmentsPage;
